import React from 'react'

import ChartPlaceholder from '../ChartPlaceholder'
import AverageFooter from '../AverageFooter'
import Tooltip from '../Tooltip'
import Legend from '../Legend'
import { getAverageData } from '../../helpers'
import { LegendData } from '../../types'

import * as Styled from './Chart.styled'

type BaseProps = {
  data?: any
  hasData?: boolean
  graphHeight?: number
}

type WithLegendProps = {
  title?: never
  tooltip?: never
  legend: LegendData
}

type WithTitleProps = {
  title: string | React.ReactNode
  tooltip?: string | React.ReactNode
  legend?: never
}

type WithAverageProps = {
  withAverage?: boolean
  averageHiddenOnMobile?: boolean
  isAverageCurrency?: boolean
  averageCurrencyType?: string
  averageMarginLeft?: number
  calculateAverage?: (data?: any) => number
}

type Props = Partial<WithLegendProps | WithTitleProps> & WithAverageProps & BaseProps

const Chart: React.FC<Props> = ({
  title,
  tooltip,
  legend,
  graphHeight,
  data,
  hasData,
  children,
  ...rest
}) => {
  const {
    isAverageCurrency,
    withAverage,
    averageMarginLeft,
    averageCurrencyType,
    averageHiddenOnMobile = true,
    calculateAverage
  } = rest
  const getAverage = calculateAverage ? () => calculateAverage(data) : () => getAverageData(data)
  const averageValue = withAverage || calculateAverage ? getAverage() : undefined

  const hasExistingData = hasData || !!data

  return (
    <Styled.Container isNoData={!hasExistingData}>
      <Styled.Header>
        {legend ? (
          <Legend data={legend} hasData={hasExistingData} />
        ) : (
          <Styled.Title>
            {title ?? <>&nbsp;</>}
            {tooltip && (
              <>
                &nbsp;<Styled.Tooltip as={Tooltip}>{tooltip}</Styled.Tooltip>
              </>
            )}
          </Styled.Title>
        )}
      </Styled.Header>
      <Styled.GraphContainer height={graphHeight}>
        {hasExistingData ? children : <ChartPlaceholder />}
      </Styled.GraphContainer>
      {!!averageValue && (
        <AverageFooter
          amount={averageValue}
          marginLeft={averageMarginLeft}
          isCurrency={isAverageCurrency || !!averageCurrencyType}
          currency={averageCurrencyType}
          hideDescriptionOnMobile={averageHiddenOnMobile}
        />
      )}
    </Styled.Container>
  )
}

export default Chart
